import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Diamond, Sparkle, ShieldCheck, Palette, TextAa, Confetti } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const KIND_META = {
  celebration: { label: "Celebration Picker", icon: Confetti, blurb: "Own a set, then choose which celebration ALWAYS plays on your wins." },
  flair_color: { label: "Name Colors", icon: TextAa, blurb: "Color your name in the header and dashboard." },
  flair_title: { label: "Titles", icon: Sparkle, blurb: "A title badge next to your name." },
  theme: { label: "App Themes", icon: Palette, blurb: "Swap the app's accent colors." },
};

export const GemShopCard = () => {
  const { refreshMe, user } = useAuth();
  const [shop, setShop] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const teamRole = ["team_content", "team_dev"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/gems/shop"); setShop(data); }
    catch { setShop(null); }
  };
  useEffect(() => { if (!teamRole) load(); }, [teamRole]);
  if (!shop) return null;

  const buy = async (p) => {
    setBusy(p.id);
    try {
      await api.post("/gems/buy", { perk_id: p.id });
      toast.success(p.kind === "consumable" ? "🛡️ Streak shield added!" : `✨ ${p.name} unlocked & equipped!`);
      await Promise.all([load(), refreshMe()]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const select = async (kind, p) => {
    setBusy(p ? p.id : `default-${kind}`);
    try {
      await api.post("/gems/select", { kind, perk_id: p ? p.id : null });
      toast.success(p ? `${p.name} equipped` : "Back to the default");
      await Promise.all([load(), refreshMe()]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const spendable = shop.spendable_emeralds;
  const shield = shop.perks.find(p => p.kind === "consumable");
  const groups = ["celebration", "flair_color", "flair_title", "theme"];

  return (
    <div className="card mb-3" data-testid="gem-shop-card">
      <button className="w-full flex items-center justify-between gap-3 bg-transparent border-0 cursor-pointer p-0 text-left"
        onClick={() => setOpen(o => !o)} data-testid="gem-shop-toggle">
        <div className="flex items-center gap-2 min-w-0">
          <Diamond size={22} weight="fill" color="#34D399" className="shrink-0" />
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">Gem Shop</div>
            <div className="text-xs text-[#000000]">Spend emeralds on perks — celebrations, flair & themes</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full font-display font-bold text-sm"
            style={{ background: "#34D3991f", border: "1px solid #34D39980", color: "#059669" }}
            data-testid="gem-shop-balance">
            <Diamond size={14} weight="fill" color="#34D399" /> {spendable}
          </span>
          <span className="text-[#000000] text-sm">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-5" data-testid="gem-shop-body">
          {shop.rubies > 0 && (
            <p className="text-xs text-[#000000] -mt-1">💎 Short on emeralds? A ruby auto-breaks into 5 emeralds when you buy. Diamonds are forever — they never break.</p>
          )}

          {/* Streak shield for a gem */}
          {shield && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#C4C8CE] bg-[#F5F6F8]">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck size={20} weight="fill" color="#0A6B7A" className="shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Streak Shield <span className="text-xs text-[#000000]">({shop.streak_freezes}/{shop.shield_max} held)</span></div>
                  <div className="text-xs text-[#000000]">Auto-saves your streak if you miss a day</div>
                </div>
              </div>
              <button className="btn-secondary text-xs px-3 py-1.5 shrink-0" data-testid="gem-buy-shield"
                disabled={busy === shield.id || shop.streak_freezes >= shop.shield_max || spendable < shield.cost}
                onClick={() => buy(shield)}>
                {shop.streak_freezes >= shop.shield_max ? "Shields full" : `1 emerald`}
              </button>
            </div>
          )}

          {groups.map(kind => {
            const meta = KIND_META[kind];
            const perks = shop.perks.filter(p => p.kind === kind);
            const selected = shop.selected[kind === "theme" ? "theme" : kind === "celebration" ? "celebration" : kind];
            const Icon = meta.icon;
            return (
              <div key={kind}>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#000000] mb-0.5">
                  <Icon size={15} weight="duotone" /> {meta.label}
                </div>
                <p className="text-xs text-[#000000] mb-2">{meta.blurb}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={"px-3 py-1.5 rounded-full text-xs font-display font-bold border-2 transition-colors " +
                      (!selected ? "border-[#CCFF00] bg-[rgba(204,255,0,0.2)] text-[#000000]" : "border-[#C4C8CE] bg-white text-[#000000] hover:border-[#94979C]")}
                    disabled={busy === `default-${kind}`}
                    onClick={() => selected && select(kind, null)}
                    data-testid={`gem-default-${kind}`}>
                    {kind === "celebration" ? "🎲 Surprise me" : "Default"}{!selected && " ✓"}
                  </button>
                  {perks.map(p => {
                    const inUse = selected === p.value;
                    return p.owned ? (
                      <button key={p.id}
                        className={"px-3 py-1.5 rounded-full text-xs font-display font-bold border-2 transition-colors flex items-center gap-1.5 " +
                          (inUse ? "border-[#CCFF00] bg-[rgba(204,255,0,0.2)] text-[#000000]" : "border-[#C4C8CE] bg-white text-[#000000] hover:border-[#94979C]")}
                        disabled={busy === p.id || inUse}
                        onClick={() => select(kind, p)}
                        data-testid={`gem-use-${p.id}`}>
                        {kind === "flair_color" && <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.value }} />}
                        {p.name}{inUse && " ✓"}
                      </button>
                    ) : (
                      <button key={p.id}
                        className="px-3 py-1.5 rounded-full text-xs font-display font-bold border-2 border-dashed border-[#C4C8CE] bg-[#F5F6F8] text-[#000000] hover:border-[#34D399] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        disabled={busy === p.id || spendable < p.cost}
                        title={spendable < p.cost ? `Need ${p.cost} emerald${p.cost > 1 ? "s" : ""}` : `Unlock for ${p.cost} emerald${p.cost > 1 ? "s" : ""}`}
                        onClick={() => buy(p)}
                        data-testid={`gem-buy-${p.id}`}>
                        {kind === "flair_color" && <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.value }} />}
                        {p.name}
                        <span className="flex items-center gap-0.5 text-[#059669]"><Diamond size={11} weight="fill" color="#34D399" />{p.cost}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
